import { supabase } from "@/integrations/supabase/client";
import { telegramBot } from "@/integrations/telegram/client";
import { toast } from "@/hooks/use-toast";
import { Client, ClientFormValues } from "@/types/client";
import { Dictionary, DictionaryFormValues, DictionaryItem, DictionaryItemFormValues } from "@/types/dictionary";
import { Vacancy, VacancyFormValues } from "@/types/vacancy";

// General CRUD operations for admin entities
export const api = {
  // Products
  products: {
    getAll: async () => {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        toast({
          title: "Error fetching products",
          description: error.message,
          variant: "destructive",
        });
        return [];
      }
      
      return data;
    },
    
    getById: async (id: string) => {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) {
        toast({
          title: "Error fetching product",
          description: error.message,
          variant: "destructive",
        });
        return null;
      }
      
      return data;
    },
    
    create: async (product: any) => {
      const { data, error } = await supabase
        .from('products')
        .insert([product])
        .select()
        .single();
      
      if (error) {
        toast({
          title: "Error creating product",
          description: error.message,
          variant: "destructive",
        });
        return null;
      }
      
      toast({
        title: "Product created",
        description: "The product has been created successfully",
      });
      
      return data;
    },
    
    update: async (id: string, product: any) => {
      const { data, error } = await supabase
        .from('products')
        .update(product)
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        toast({
          title: "Error updating product",
          description: error.message,
          variant: "destructive",
        });
        return null;
      }
      
      toast({
        title: "Product updated",
        description: "The product has been updated successfully",
      });
      
      return data;
    },
    
    delete: async (id: string) => {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', id);
      
      if (error) {
        toast({
          title: "Error deleting product",
          description: error.message,
          variant: "destructive",
        });
        return false;
      }
      
      toast({
        title: "Product deleted",
        description: "The product has been deleted successfully",
      });
      
      return true;
    }
  },
  
  // Services
  services: {
    getAll: async () => {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        toast({
          title: "Error fetching services",
          description: error.message,
          variant: "destructive",
        });
        return [];
      }
      
      return data;
    },
    
    getById: async (id: string) => {
      const { data, error } = await supabase
        .from('services')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) {
        toast({
          title: "Error fetching service",
          description: error.message,
          variant: "destructive",
        });
        return null;
      }
      
      return data;
    },
    
    create: async (service: any) => {
      const { data, error } = await supabase
        .from('services')
        .insert([service])
        .select()
        .single();
      
      if (error) {
        toast({
          title: "Error creating service",
          description: error.message,
          variant: "destructive",
        });
        return null;
      }
      
      toast({
        title: "Service created",
        description: "The service has been created successfully",
      });
      
      return data;
    },
    
    update: async (id: string, service: any) => {
      const { data, error } = await supabase
        .from('services')
        .update(service)
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        toast({
          title: "Error updating service",
          description: error.message,
          variant: "destructive",
        });
        return null;
      }
      
      toast({
        title: "Service updated",
        description: "The service has been updated successfully",
      });
      
      return data;
    },
    
    delete: async (id: string) => {
      const { error } = await supabase
        .from('services')
        .delete()
        .eq('id', id);
      
      if (error) {
        toast({
          title: "Error deleting service",
          description: error.message,
          variant: "destructive",
        });
        return false;
      }
      
      toast({
        title: "Service deleted",
        description: "The service has been deleted successfully",
      });
      
      return true;
    }
  },
  
  // Clients
  clients: {
    getAll: async () => {
      console.log('Fetching all clients from Supabase...');
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching clients:', error);
        toast({
          title: "Error fetching clients",
          description: error.message,
          variant: "destructive",
        });
        return [];
      }
      
      console.log('Fetched clients:', data);
      return data as Client[];
    },
    
    getById: async (id: string) => {
      console.log('Fetching client by ID:', id);
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) {
        console.error('Error fetching client:', error);
        toast({
          title: "Error fetching client",
          description: error.message,
          variant: "destructive",
        });
        return null;
      }
      
      console.log('Fetched client:', data);
      return data as Client;
    },
    
    create: async (client: ClientFormValues) => {
      console.log('Creating new client:', client);
      const { data, error } = await supabase
        .from('clients')
        .insert([client])
        .select()
        .single();
      
      if (error) {
        console.error('Error creating client:', error);
        toast({
          title: "Error creating client",
          description: error.message,
          variant: "destructive",
        });
        return null;
      }
      
      console.log('Created client:', data);
      toast({
        title: "Client created",
        description: "Client has been created successfully",
      });
      
      return data as Client;
    },
    
    update: async (id: string, client: ClientFormValues) => {
      console.log('Updating client:', { id, client });
      const { data, error } = await supabase
        .from('clients')
        .update(client)
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        console.error('Error updating client:', error);
        toast({
          title: "Error updating client",
          description: error.message,
          variant: "destructive",
        });
        return null;
      }
      
      console.log('Updated client:', data);
      toast({
        title: "Client updated",
        description: "Client has been updated successfully",
      });
      
      return data as Client;
    },
    
    delete: async (id: string) => {
      console.log('Deleting client:', id);
      const { error } = await supabase
        .from('clients')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error('Error deleting client:', error);
        toast({
          title: "Error deleting client",
          description: error.message,
          variant: "destructive",
        });
        return false;
      }
      
      console.log('Client deleted successfully');
      toast({
        title: "Client deleted",
        description: "Client has been deleted successfully",
      });
      
      return true;
    }
  },
  
  // News
  news: {
    getAll: async () => {
      const { data, error } = await supabase
        .from('news')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        toast({
          title: "Error fetching news",
          description: error.message,
          variant: "destructive",
        });
        return [];
      }
      
      return data;
    },
    
    getById: async (id: string) => {
      const { data, error } = await supabase
        .from('news')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) {
        toast({
          title: "Error fetching news article",
          description: error.message,
          variant: "destructive",
        });
        return null;
      }
      
      return data;
    },
    
    create: async (newsItem: any) => {
      // Ensure published_date is a string if it's a Date object
      const formattedNewsItem = {
        ...newsItem,
        published_date: newsItem.published_date instanceof Date 
          ? newsItem.published_date.toISOString() 
          : newsItem.published_date
      };
      
      const { data, error } = await supabase
        .from('news')
        .insert([formattedNewsItem])
        .select()
        .single();
      
      if (error) {
        toast({
          title: "Error creating news article",
          description: error.message,
          variant: "destructive",
        });
        return null;
      }
      
      toast({
        title: "News article created",
        description: "The news article has been created successfully",
      });
      
      return data;
    },
    
    update: async (id: string, newsItem: any) => {
      // Ensure published_date is a string if it's a Date object
      const formattedNewsItem = {
        ...newsItem,
        published_date: newsItem.published_date instanceof Date 
          ? newsItem.published_date.toISOString() 
          : newsItem.published_date
      };
      
      const { data, error } = await supabase
        .from('news')
        .update(formattedNewsItem)
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        toast({
          title: "Error updating news article",
          description: error.message,
          variant: "destructive",
        });
        return null;
      }
      
      toast({
        title: "News article updated",
        description: "The news article has been updated successfully",
      });
      
      return data;
    },
    
    delete: async (id: string) => {
      const { error } = await supabase
        .from('news')
        .delete()
        .eq('id', id);
      
      if (error) {
        toast({
          title: "Error deleting news article",
          description: error.message,
          variant: "destructive",
        });
        return false;
      }
      
      toast({
        title: "News article deleted",
        description: "The news article has been deleted successfully",
      });
      
      return true;
    }
  },
  
  // Settings
  settings: {
    getAll: async () => {
      const { data, error } = await supabase
        .from('settings')
        .select('*')
        .order('key');
      
      if (error) {
        toast({
          title: "Error fetching settings",
          description: error.message,
          variant: "destructive",
        });
        return [];
      }
      
      return data;
    },
    
    getByKey: async (key: string) => {
      const { data, error } = await supabase
        .from('settings')
        .select('*')
        .eq('key', key)
        .single();
      
      if (error) {
        console.error("Error fetching setting:", error);
        return null;
      }
      
      return data;
    },
    
    update: async (key: string, value: string) => {
      const { data, error } = await supabase
        .from('settings')
        .update({ value, updated_at: new Date().toISOString() })
        .eq('key', key)
        .select()
        .single();
      
      if (error) {
        toast({
          title: "Error updating setting",
          description: error.message,
          variant: "destructive",
        });
        return null;
      }
      
      toast({
        title: "Setting updated",
        description: `${key} has been updated successfully`,
      });
      
      return data;
    }
  },

  // Contact
  contact: {
    getAll: async () => {
      const { data, error } = await supabase
        .from('contact_messages')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        toast({
          title: "Error fetching messages",
          description: error.message,
          variant: "destructive",
        });
        return [];
      }
      
      return data;
    },
    
    getById: async (id: string) => {
      const { data, error } = await supabase
        .from('contact_messages')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) {
        toast({
          title: "Error fetching message",
          description: error.message,
          variant: "destructive",
        });
        return null;
      }
      
      return data;
    },
    
    delete: async (id: string) => {
      const { error } = await supabase
        .from('contact_messages')
        .delete()
        .eq('id', id);
      
      if (error) {
        toast({
          title: "Error deleting message",
          description: error.message,
          variant: "destructive",
        });
        return false;
      }
      
      toast({
        title: "Message deleted",
        description: "The message has been deleted successfully",
      });
      
      return true;
    },

    sendMessage: async (data: {
      name: string;
      email: string;
      phone?: string;
      company?: string;
      message: string;
    }) => {
      try {
        // Сохраняем сообщение в базу данных
        const { error: dbError } = await supabase
          .from('contact_messages')
          .insert([data]);

        if (dbError) throw dbError;

        // Отправляем уведомление в Telegram через Edge Function
        const response = await fetch(
          'https://vzxdvmjeolovijlrrpwx.supabase.co/functions/v1/send-telegram-message',
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
            },
            body: JSON.stringify(data)
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to send message');
        }

        toast({
          title: "Сообщение отправлено",
          description: "Ваше сообщение успешно отправлено",
        });
        return true;
      } catch (error) {
        console.error('Error sending message:', error);
        toast({
          title: "Ошибка отправки сообщения",
          description: error instanceof Error ? error.message : 'Ошибка при отправке сообщения',
          variant: "destructive",
        });
        return false;
      }
    }
  },

  // Subscribers
  subscribers: {
    getAll: async () => {
      const { data, error } = await supabase
        .from('subscribers')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        toast({
          title: "Error fetching subscribers",
          description: error.message,
          variant: "destructive",
        });
        return [];
      }
      
      return data;
    },
    
    getById: async (id: string) => {
      const { data, error } = await supabase
        .from('subscribers')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) {
        toast({
          title: "Error fetching subscriber",
          description: error.message,
          variant: "destructive",
        });
        return null;
      }
      
      return data;
    },
    
    delete: async (id: string) => {
      const { error } = await supabase
        .from('subscribers')
        .delete()
        .eq('id', id);
      
      if (error) {
        toast({
          title: "Error deleting subscriber",
          description: error.message,
          variant: "destructive",
        });
        return false;
      }
      
      toast({
        title: "Subscriber deleted",
        description: "The subscriber has been deleted successfully",
      });
      
      return true;
    },

    subscribe: async (email: string) => {
      try {
        // Check if subscriber already exists
        const { data: existingSubscriber, error: checkError } = await supabase
          .from('subscribers')
          .select('id')
          .eq('email', email)
          .single();

        if (checkError && checkError.code !== 'PGRST116') {
          throw checkError;
        }

        if (existingSubscriber) {
          throw new Error('Вы уже подписаны на рассылку');
        }

        // Add new subscriber
        const { error: insertError } = await supabase
          .from('subscribers')
          .insert([{ email }]);

        if (insertError) throw insertError;

        toast({
          title: "Подписка оформлена",
          description: "Вы успешно подписались на рассылку",
        });
        return true;
      } catch (error) {
        console.error('Error subscribing:', error);
        toast({
          title: "Ошибка подписки",
          description: error instanceof Error ? error.message : 'Ошибка при оформлении подписки',
          variant: "destructive",
        });
        return false;
      }
    },

    notifyTelegram: async (email: string, subscription_source?: string) => {
      // Get the current URL as the source if not provided
      const source = subscription_source || window.location.href;
      
      const { data, error } = await supabase.functions.invoke('subscriber-notification', {
        body: { email, subscription_source: source }
      });
      
      if (error) throw error;
      return data;
    }
  },

  // Dictionaries
  dictionaries: {
    getAll: async () => {
      console.log('Fetching dictionaries...');
      const { data, error } = await supabase
        .from('dictionaries')
        .select(`
          *,
          dictionary_items (
            id,
            dictionary_id,
            name,
            code,
            description,
            is_active,
            created_at,
            updated_at
          )
        `)
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching dictionaries:', error);
        toast({
          title: "Error fetching dictionaries",
          description: error.message,
          variant: "destructive",
        });
        return [];
      }
      
      console.log('Fetched dictionaries:', data);
      return data.map(dictionary => ({
        ...dictionary,
        items: (dictionary.dictionary_items || []).map(item => ({
          ...item,
          dictionary_id: dictionary.id
        }))
      })) as Dictionary[];
    },
    
    getById: async (id: string) => {
      const { data, error } = await supabase
        .from('dictionaries')
        .select(`
          *,
          dictionary_items (
            id,
            dictionary_id,
            name,
            code,
            description,
            is_active,
            created_at,
            updated_at
          )
        `)
        .eq('id', id)
        .single();
      
      if (error) {
        toast({
          title: "Error fetching dictionary",
          description: error.message,
          variant: "destructive",
        });
        return null;
      }
      
      return {
        ...data,
        items: (data.dictionary_items || []).map(item => ({
          ...item,
          dictionary_id: data.id
        }))
      } as Dictionary;
    },
    
    create: async (dictionary: DictionaryFormValues) => {
      console.log('Creating dictionary with data:', dictionary);
      const dataToInsert = {
        name: dictionary.name,
        code: dictionary.code,
        description: dictionary.description || null,
      };
      console.log('Data to insert:', dataToInsert);
      
      const { data, error } = await supabase
        .from('dictionaries')
        .insert([dataToInsert])
        .select()
        .single();

      if (error) {
        console.error('Error creating dictionary:', error);
        toast({
          title: "Error creating dictionary",
          description: error.message,
          variant: "destructive",
        });
        throw error;
      }
      
      console.log('Created dictionary:', data);
      toast({
        title: "Dictionary created",
        description: "Dictionary has been created successfully",
      });
      
      return {
        ...data,
        items: []
      } as Dictionary;
    },
    
    update: async (id: string, dictionary: DictionaryFormValues) => {
      const { error } = await supabase
        .from('dictionaries')
        .update({
          name: dictionary.name,
          code: dictionary.code,
          description: dictionary.description,
          updated_at: new Date().toISOString()
        })
        .eq('id', id);
      
      if (error) {
        console.error('Error updating dictionary:', error);
        toast({
          title: "Error updating dictionary",
          description: error.message,
          variant: "destructive",
        });
        throw error;
      }
      
      toast({
        title: "Dictionary updated",
        description: "Dictionary has been updated successfully",
      });
      
      return dictionary as Dictionary;
    },
    
    delete: async (id: string) => {
      const { error } = await supabase
        .from('dictionaries')
        .delete()
        .eq('id', id);
      
      if (error) {
        toast({
          title: "Error deleting dictionary",
          description: error.message,
          variant: "destructive",
        });
        return false;
      }
      
      toast({
        title: "Dictionary deleted",
        description: "Dictionary has been deleted successfully",
      });
      
      return true;
    },

    // Dictionary Items
    createItem: async (dictionaryId: string, item: DictionaryItemFormValues) => {
      const { data, error } = await supabase
        .from('dictionary_items')
        .insert([{ ...item, dictionary_id: dictionaryId }])
        .select()
        .single();
      
      if (error) {
        toast({
          title: "Error creating dictionary item",
          description: error.message,
          variant: "destructive",
        });
        return null;
      }
      
      toast({
        title: "Dictionary item created",
        description: "Dictionary item has been created successfully",
      });
      
      return data as DictionaryItem;
    },
    
    updateItem: async (dictionaryId: string, itemId: string, item: DictionaryItemFormValues) => {
      const { data, error } = await supabase
        .from('dictionary_items')
        .update(item)
        .eq('id', itemId)
        .eq('dictionary_id', dictionaryId)
        .select()
        .single();
      
      if (error) {
        toast({
          title: "Error updating dictionary item",
          description: error.message,
          variant: "destructive",
        });
        return null;
      }
      
      toast({
        title: "Dictionary item updated",
        description: "Dictionary item has been updated successfully",
      });
      
      return data as DictionaryItem;
    },
    
    deleteItem: async (dictionaryId: string, itemId: string) => {
      const { error } = await supabase
        .from('dictionary_items')
        .delete()
        .eq('id', itemId)
        .eq('dictionary_id', dictionaryId);
      
      if (error) {
        toast({
          title: "Error deleting dictionary item",
          description: error.message,
          variant: "destructive",
        });
        return false;
      }
      
      toast({
        title: "Dictionary item deleted",
        description: "Dictionary item has been deleted successfully",
      });
      
      return true;
    }
  },
  
  // Vacancies
  vacancies: {
    getAll: async () => {
      console.log('Fetching all vacancies from Supabase...');
      const { data, error } = await supabase
        .from('vacancies')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        console.error('Error fetching vacancies:', error);
        toast({
          title: "Error fetching vacancies",
          description: error.message,
          variant: "destructive",
        });
        return [];
      }
      
      console.log('Fetched vacancies:', data);
      return data.map(vacancy => ({
        id: vacancy.id,
        title: vacancy.title,
        description: vacancy.description,
        requirements: vacancy.requirements,
        location: vacancy.location,
        salaryRange: vacancy.salary_range,
        status: vacancy.status as 'draft' | 'published',
        created_at: vacancy.created_at,
        updated_at: vacancy.updated_at
      })) as Vacancy[];
    },
    
    getById: async (id: string) => {
      console.log('Fetching vacancy by ID:', id);
      const { data, error } = await supabase
        .from('vacancies')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) {
        console.error('Error fetching vacancy:', error);
        toast({
          title: "Error fetching vacancy",
          description: error.message,
          variant: "destructive",
        });
        return null;
      }
      
      console.log('Fetched vacancy:', data);
      return {
        id: data.id,
        title: data.title,
        description: data.description,
        requirements: data.requirements,
        location: data.location,
        salaryRange: data.salary_range,
        status: data.status as 'draft' | 'published',
        created_at: data.created_at,
        updated_at: data.updated_at
      } as Vacancy;
    },
    
    create: async (vacancy: VacancyFormValues) => {
      console.log('Creating new vacancy:', vacancy);
      const { data, error } = await supabase
        .from('vacancies')
        .insert([{
          title: vacancy.title,
          description: vacancy.description,
          requirements: vacancy.requirements,
          location: vacancy.location,
          salary_range: vacancy.salaryRange,
          status: vacancy.status
        }])
        .select()
        .single();
      
      if (error) {
        console.error('Error creating vacancy:', error);
        toast({
          title: "Error creating vacancy",
          description: error.message,
          variant: "destructive",
        });
        return null;
      }
      
      console.log('Created vacancy:', data);
      toast({
        title: "Vacancy created",
        description: "Vacancy has been created successfully",
      });
      
      return {
        id: data.id,
        title: data.title,
        description: data.description,
        requirements: data.requirements,
        location: data.location,
        salaryRange: data.salary_range,
        status: data.status as 'draft' | 'published',
        created_at: data.created_at,
        updated_at: data.updated_at
      } as Vacancy;
    },
    
    update: async (id: string, vacancy: VacancyFormValues) => {
      console.log('Updating vacancy:', { id, vacancy });
      const { data, error } = await supabase
        .from('vacancies')
        .update({
          title: vacancy.title,
          description: vacancy.description,
          requirements: vacancy.requirements,
          location: vacancy.location,
          salary_range: vacancy.salaryRange,
          status: vacancy.status
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        console.error('Error updating vacancy:', error);
        toast({
          title: "Error updating vacancy",
          description: error.message,
          variant: "destructive",
        });
        return null;
      }
      
      console.log('Updated vacancy:', data);
      toast({
        title: "Vacancy updated",
        description: "Vacancy has been updated successfully",
      });
      
      return {
        id: data.id,
        title: data.title,
        description: data.description,
        requirements: data.requirements,
        location: data.location,
        salaryRange: data.salary_range,
        status: data.status as 'draft' | 'published',
        created_at: data.created_at,
        updated_at: data.updated_at
      } as Vacancy;
    },
    
    delete: async (id: string) => {
      console.log('Deleting vacancy:', id);
      const { error } = await supabase
        .from('vacancies')
        .delete()
        .eq('id', id);
      
      if (error) {
        console.error('Error deleting vacancy:', error);
        toast({
          title: "Error deleting vacancy",
          description: error.message,
          variant: "destructive",
        });
        return false;
      }
      
      console.log('Vacancy deleted successfully');
      toast({
        title: "Vacancy deleted",
        description: "Vacancy has been deleted successfully",
      });
      
      return true;
    }
  },

  // Vacancy Applications
  vacancyApplications: {
    submit: async (vacancyId: string, data: {
      name: string;
      email: string;
      phone?: string;
      cover_letter?: string;
      resume?: File;
      vacancy_title: string;
    }) => {
      try {
        let resumeUrl: string | null = null;

        // Upload resume if provided
        if (data.resume) {
          const fileExt = data.resume.name.split('.').pop();
          const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
          const filePath = `${vacancyId}/${fileName}`;

          const { error: uploadError } = await supabase.storage
            .from('resumes')
            .upload(filePath, data.resume);

          if (uploadError) {
            throw new Error(`Error uploading resume: ${uploadError.message}`);
          }

          // Store the file path instead of URL
          resumeUrl = filePath;
        }

        // Create application record
        const { data: application, error } = await supabase
          .from('vacancy_applications')
          .insert([{
            vacancy_id: vacancyId,
            vacancy_title: data.vacancy_title,
            name: data.name,
            email: data.email,
            phone: data.phone || null,
            cover_letter: data.cover_letter || null,
            resume_url: resumeUrl,
            status: 'new'
          }])
          .select()
          .single();

        if (error) {
          throw new Error(`Error submitting application: ${error.message}`);
        }

        return application;
      } catch (error: any) {
        console.error('Error submitting vacancy application:', error);
        toast({
          title: "Ошибка отправки заявки",
          description: error.message,
          variant: "destructive",
        });
        throw error;
      }
    },

    // Add a new method to get signed URL for resume
    getResumeUrl: async (filePath: string) => {
      try {
        const { data: { signedUrl }, error } = await supabase.storage
          .from('resumes')
          .createSignedUrl(filePath, 60 * 60 * 24 * 7); // URL valid for 7 days

        if (error) {
          throw new Error(`Error generating signed URL: ${error.message}`);
        }

        return signedUrl;
      } catch (error: any) {
        console.error('Error getting resume URL:', error);
        throw error;
      }
    },

    getAll: async () => {
      const { data, error } = await supabase
        .from('vacancy_applications')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) {
        toast({
          title: "Error fetching applications",
          description: error.message,
          variant: "destructive",
        });
        return [];
      }
      
      return data;
    },

    getById: async (id: string) => {
      const { data, error } = await supabase
        .from('vacancy_applications')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) {
        toast({
          title: "Error fetching application",
          description: error.message,
          variant: "destructive",
        });
        return null;
      }
      
      return data;
    },

    updateStatus: async (id: string, status: string) => {
      const { data, error } = await supabase
        .from('vacancy_applications')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      
      if (error) {
        toast({
          title: "Error updating application status",
          description: error.message,
          variant: "destructive",
        });
        return null;
      }
      
      toast({
        title: "Application status updated",
        description: "The application status has been updated successfully",
      });
      
      return data;
    },

    delete: async (id: string) => {
      const { error } = await supabase
        .from('vacancy_applications')
        .delete()
        .eq('id', id);
      
      if (error) {
        toast({
          title: "Error deleting application",
          description: error.message,
          variant: "destructive",
        });
        throw error;
      }
      
      toast({
        title: "Application deleted",
        description: "The application has been deleted successfully",
      });
    }
  }
};
